import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  SalesforceConnection,
  SalesforceConnectionEdition,
  SalesforceLoginMethod,
} from '@modules/salesforce/models';

import { ConnectionSchema } from '@modules/salesforce/shared/data/connection-schema';
import { useSFConnectionState } from '@contexts/sf-connection-context';

import { getConnectionEnv } from '@modules/salesforce/utils/utils';

const useEditConnectionForm = () => {
  const { selectedConnection } = useSFConnectionState();

  const getDefaultValues = (connection: SalesforceConnection) => {
    const defaultValues = {
      ...connection,
      name: connection?.name,
      salesforceConnectionEdition: getConnectionEnv(connection),
      loginMethod: connection?.loginMethod ?? SalesforceLoginMethod.OAUTH,
      isAdmin: connection?.isAdmin ?? false,
      username: connection?.username,
      password: connection?.password,
      securityToken: connection?.securityToken,
      clientId: connection?.clientId,
      clientSecret: connection?.clientSecret,
      callbackUrl: connection?.callbackUrl,
      accessToken: connection?.accessToken,
      refreshToken: connection?.refreshToken,
      instanceUrl: connection?.instanceUrl,
      expirationDate: connection?.expirationDate,
    };

    return defaultValues;
  };

  const {
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    formState: { isValid, dirtyFields },
  } = useForm({
    resolver: yupResolver(ConnectionSchema),
    defaultValues: getDefaultValues(selectedConnection),
  });

  const handleUpdateFormValue = (fieldId: any, fieldValue: any) => {
    if (fieldId === 'loginMethod' && fieldValue !== watch('loginMethod')) {
      setValue('callbackUrl', null);
      setValue('accessToken', null);
      setValue('refreshToken', null);
      setValue('password', null);
      setValue('securityToken', null);
    }

    // Clear scratch org fields when switching away from scratch org
    if (
      fieldId === 'salesforceConnectionEdition' &&
      fieldValue !== SalesforceConnectionEdition.SCRATCH_ORG
    ) {
      setValue('instanceUrl', null);
      setValue('expirationDate', null);
    }

    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  // Determine if only the name field is dirty (indicating a simple name change)
  const areSaveOnlyChanges = () => {
    return Object.keys(dirtyFields).length === 1 && dirtyFields.name === true;
  };

  useEffect(() => {
    reset({
      ...getDefaultValues(selectedConnection),
    });
  }, [selectedConnection, reset]);

  return {
    isValid,
    isSaveOnlyMode: areSaveOnlyChanges(),
    loginMethod: watch('loginMethod') as SalesforceLoginMethod,
    handleSubmit,
    values: getValues(),
    handleUpdateFormValue,
    watch,
  };
};

export { useEditConnectionForm };
